import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';
import ROUTES from '@CONSTANTS/ROUTES.constant';
import DESIGN from '@CONSTANTS/DESIGN.constant';

import { notify } from '@NOTIFICATION/Notificator';

import {
    BrandStep,
    DocumentStep,
    OptionsStep,
    RegionStep,
    SelectContentStep,
    TypeStep,
} from '@REDUCERS';

import { useGetContent } from '@SERVICES';
import useStepsValidation from '@VALIDATION/hooks/useStepsValidation';
import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';

import DataLoader from '@COMPONENTS/SHARED/DataLoader';
import StepNavigation from '@COMPONENTS/COMMON/navigation/StepNavigation';
import SearchInput from '@COMPONENTS/COMMON/inputs/input/SearchInput';

import { generationModes } from '@PAGES/manager/options/Options.page';
import Content from '@PAGES/manager/select-content/components/Content';

// import TEST_WINDOW from '@PAGES/manager/select-content/components/TEST_WINDOW/TEST_WINDOW';

import styles from './SelectContent.module.scss';

const cx: CX = classnames.bind(styles);

const defaultData = {
    data: [],
    meta: {
        tags: [],
    },
};

function SelectContentPage() {
    const {
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps();

    const documentStep: DocumentStep = documentGenerationStepsServices.getStepData(GenerationSteps.DOCUMENT);
    const typeStep: TypeStep = documentGenerationStepsServices.getStepData(GenerationSteps.TYPE);
    const brandStep: BrandStep = documentGenerationStepsServices.getStepData(GenerationSteps.BRAND);
    const locationStep: RegionStep = documentGenerationStepsServices.getStepData(GenerationSteps.LOCATION);
    const optionsStep: OptionsStep = documentGenerationStepsServices.getStepData(GenerationSteps.OPTIONS);
    const selectContentStep: SelectContentStep = documentGenerationStepsServices.getStepData(GenerationSteps.SELECT_CONTENT);

    const {
        data: contentData = defaultData, isLoading, isError, error,
    } = useGetContent({
        document: documentStep.data?.id,
        brand: brandStep.data?.id,
        country: locationStep.data?.id,
        type: typeStep.data?.id,
    });

    const {
        data: chaptersData = [],
        meta: {
            tags,
        },
    } = contentData;

    const canActivateStep = useStepsValidation();

    const navigate = useNavigate();

    const tagsIds = selectContentStep.data?.tagsIds || [];

    // Removed isOnlyRequiredSections - no longer needed
    const [selectedTagsIds, setSelectedTagsIds] = useState<Set<number>>(new Set(tagsIds));

    const [searchValue, setSearchValue] = useState<string>('');

    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');

    // DEBOUNCE INPUT
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 250);

        return () => {
            clearTimeout(timer);
        };
    }, [searchValue]);

    return (
        <div className={cx('page')}>
            <h1>Select content</h1>

            <DataLoader
                isLoading={isLoading}
                isError={isError}
                error={error}
            >
                <div className={cx('content-wrapper')}>
                    {/* Integrated search */}
                    <div className={cx('search-section')}>
                        <SearchInput
                            value={searchValue}
                            placeholder="Search for chapter or section..."
                            onChange={setSearchValue}
                            isLoading={false}
                            onFocus={() => { }}
                        />
                    </div>

                    <Content
                        chaptersData={chaptersData}
                        selectedTagsIds={selectedTagsIds}
                        searchValue={debouncedSearchValue}
                        setSearchValue={setSearchValue}
                    />
                </div>
            </DataLoader>

            <StepNavigation
                onNext={() => {
                    const { canActivate } = canActivateStep(GenerationSteps.DOWNLOAD_DOCUMENT);

                    const variantsIds = selectContentStep.data?.variantsIds || [];
                    const sectionsIds = selectContentStep.data?.sectionsIds || [];
                    const chaptersIds = selectContentStep.data?.chaptersIds || [];

                    const anyChapterHasBody = chaptersData.filter(
                        (chapter) => chaptersIds.includes(chapter.id),
                    ).some((chapter) => chapter.hasBody);

                    const anySectionHasBody = chaptersData.some((chapter) => chapter.sections.filter(
                        (section) => sectionsIds.includes(section.id),
                    ).some(
                        (section) => section.hasBody,
                    ));

                    const hasValidSelection = variantsIds.length > 0 || sectionsIds.length > 0 || chaptersIds.length > 0;

                    if (canActivate && hasValidSelection) {
                        navigate(ROUTES.downloadDocument.path);
                    } else {
                        notify.error('No content selected');
                    }
                }}
                nextDisabled={(() => {
                    const variantsIds = selectContentStep.data?.variantsIds || [];
                    const sectionsIds = selectContentStep.data?.sectionsIds || [];
                    const chaptersIds = selectContentStep.data?.chaptersIds || [];

                    // Enable button if any content is selected
                    return !(variantsIds.length > 0
                        || sectionsIds.length > 0
                        || chaptersIds.length > 0);
                })()}
                nextText="Continue to Download"
                sticky
            />

            {/* <TEST_WINDOW /> */}
        </div>
    );
}

export default SelectContentPage;
